// Planning · Tijdlijn (design/build/proto-tijdlijn-logic.js): balken per klant of per persoon in een venster van
// 4 maanden of 8 weken, met fases, mijlpalen, het einde van elk klantplan en de afhankelijkheden tussen taken.
// Taken met een deadline vanaf vandaag staan als balk in beeld; wat al over deadline is, telt per klant mee en hoort in
// Opschonen. De planner met Project-licentie sleept een balk om start en deadline samen te verschuiven (niet tot vóór
// vandaag) en zet opvolgers die te vroeg beginnen na hun voorganger. Medewerker of zonder licentie: alleen bekijken,
// een klik opent de taak. Met Clockify: per persoon de bezetting volgende week naast de naam, en in de tooltip van een
// balk de resterende uren (schatting min gelogd) of "geen schatting".
// Per klant staan de taken onder hun fase, als boom in de volgorde van Planner (ontwerp "Fases op het bord"); een fase
// klapt in met het pijltje, haar naam opent de faselade. Per persoon staat het pad van fases in het label achter de balk.
// Groepering, venster, kritisch pad, afhankelijkheden en zoektekst onthoudt de browser (lib/prefs.ts).
import { memo, useCallback, useEffect, useMemo, useRef, useState, type PointerEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { fmtHours, useClockifyLive } from "../lib/api-clockify";
import { useCapacityView } from "../lib/api-teams";
import { catOf } from "../lib/board";
import { faseChain, padShort, padText } from "../lib/fases";
import { allHealth } from "../lib/health";
import { useIndex, useMe, usePlanning, useTaskOps } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { isBool, isStr, oneOf, usePref } from "../lib/prefs";
import { addDays, ddmm, diffDays, firstName } from "../lib/planning";
import { rightsOf, useSettings } from "../lib/settings";
import {
  andList, dayMid, dd, findViolations, fixShifts, MONL, monthMarks, nOf, pct, spanOf, tagAt, weekMarks, windowOf,
  type DateOf, type Dated, type Violation, type Win, type WinKind,
} from "../lib/timeline";
import type { Dep, Fase, Klant, Me, PlanningData, Task, TaskOp } from "../lib/types";
import { TaskDrawer } from "../components/TaskDrawer";
import { useAppState } from "../shell/AppState";
import { LoadError } from "../shell/Layout";
import { PlanningTabs } from "./PlanningTabs";

type Group = "klant" | "persoon";
const ROW_G = 32, ROW_T = 26;

interface PlanTrack {
  track: { l: string; w: string } | null;
  end: { l: string; past: boolean } | null;
  endTag: { text: string; cls: string; left: string; right: string } | null;
  over: number; overLeft: string; overRight: string;
  milestones: { id: string; title: string; end: string; done: boolean; late: boolean; x: number }[];
}
interface GroupRow { kind: "group"; key: string; y: number; label: string; status: string; statusCls: string; coll: boolean; plan: PlanTrack | null }
/** lvl: hoe diep in de boom van fases; coll en has: ingeklapt, en heeft iets onder zich */
interface FaseRow { kind: "fase"; key: string; y: number; fase: Fase; l: number; r: number; tag: string; lvl: number; coll: boolean; has: boolean }
interface TaskRow { kind: "task"; key: string; y: number; task: Task; sub: string; owners: string; klantName: string; lvl: number }
type Row = GroupRow | FaseRow | TaskRow;

interface LayoutIn {
  data: PlanningData; idx: ReturnType<typeof useIndex>; me: Me; group: Group; W: Win; critOnly: boolean; qq: string; klant: string | null;
  collapsed: Record<string, boolean>; dated: (t: Task) => Dated; waiting: Map<string, number>;
  /** per persoon de bezetting volgende week (Clockify); null zonder capaciteit voor die persoon */
  load: (personId: string) => { text: string; cls: string } | null;
}

/** Rijen en hun hoogte, op de vastgelegde datums: tijdens het slepen verspringen of verdwijnen er geen rijen. */
function buildLayout(p: LayoutIn) {
  const { data, idx, W, qq } = p, today = data.today;
  const rows: Row[] = [], at = new Map<string, number>(), hiddenK: Klant[] = [], hiddenP: string[] = [], inView = new Set<string>();
  const filtered = !!(qq || p.critOnly || p.klant);
  let y = 0;
  const push = (r: Row, h: number) => {
    rows.push(r);
    if (r.kind !== "group") at.set(r.kind === "fase" ? r.fase.id : r.task.id, y + h / 2);
    y += h;
  };
  const klantName = (id: string) => idx.klanten.get(id)?.name || "";
  const personName = (id: string) => idx.people.get(id)?.name || (id === p.me.id ? p.me.name : "Onbekend");
  const shows = (t: Task) => {
    const d = p.dated(t);
    if (catOf(t) === "afsluiten" || d.end < today || d.start > W.to) return false;
    if ((p.critOnly && !t.critical) || (p.klant && t.projectId !== p.klant)) return false;
    return !qq || [t.title, klantName(t.projectId), padText(faseChain(t.faseId, idx.fases)), ...t.owners.map((o) => personName(o.personId))].join(" ").toLowerCase().includes(qq);
  };
  const taskRow = (t: Task, sub: string, key: string, lvl = 0) => {
    const o = t.owners.map((x) => personName(x.personId));
    push({ kind: "task", key, y, task: t, sub, owners: o.length ? o.join(", ") : "zonder eigenaar", klantName: klantName(t.projectId), lvl }, ROW_T);
    inView.add(t.id);
  };
  const groupRow = (key: string, label: string, status: string, statusCls: string, plan: PlanTrack | null) =>
    push({ kind: "group", key, y, label, status, statusCls, coll: !!p.collapsed[key], plan }, ROW_G);
  const visible = data.tasks.filter(shows);

  if (p.group === "klant") {
    // de grootste risico's eerst, zoals Klantprojecten
    for (const h of allHealth(data)) {
      const k = h.klant;
      if (p.klant && k.id !== p.klant) continue;
      const tasks = visible.filter((t) => t.projectId === k.id);
      const fases = p.critOnly ? [] : data.fases.filter((f) => f.projectId === k.id && f.progress < 1 && f.end >= today && f.start <= W.to && (!qq || (f.title + " " + k.name).toLowerCase().includes(qq)));
      const hasSpan = k.start !== k.finish;
      // zonder taken of fases in beeld: buiten beeld als het einde voorbij is of er geen looptijd is; bij zoeken of
      // alleen kritisch pad gewoon weg
      if (!tasks.length && !fases.length && (qq || p.critOnly || !(hasSpan && k.finish >= today))) {
        if (!qq && !p.critOnly) hiddenK.push(k);
        continue;
      }
      const key = "k" + k.id, { l: tl, r: tr } = spanOf(W, k.start, k.finish);
      const endIn = hasSpan && k.finish >= W.from && k.finish <= W.to, endOut = hasSpan && k.finish > W.to;
      // einde in beeld: ruit met label ernaast; einde na het venster: label op het einde van de looptijd
      const tagPos = endOut ? { cls: "p-tltag in", left: "auto", right: "0.4%" } : tr > 80 ? { cls: "p-tltag", left: "auto", right: pct(100 - tr + 1.2) } : { cls: "p-tltag", left: pct(tr + 1), right: "auto" };
      groupRow(key, k.name, h.status, "p-tls " + (h.tone === "err" ? "t-err" : h.tone === "warn" ? "t-warn" : h.tone === "ok" ? "t-ok" : "t-mute"), {
        track: hasSpan && tr > 0 ? { l: pct(tl), w: pct(Math.max(0.4, tr - tl)) } : null,
        end: endIn ? { l: pct(tr), past: k.finish <= today } : null,
        endTag: endIn || endOut ? { text: "einde " + dd(k.finish, today) + " · " + h.donePct + " % klaar", ...tagPos } : null,
        // de teller "over deadline" wijkt uit naar rechts als het einde vooraan in beeld staat
        over: h.over, overLeft: endIn && tr < 30 ? "auto" : "6px", overRight: endIn && tr < 30 ? "6px" : "auto",
        milestones: data.milestones.filter((m) => m.projectId === k.id && m.end >= W.from && m.end <= W.to)
          .map((m) => ({ id: m.id, title: m.title, end: m.end, done: m.done, late: !m.done && m.end < today, x: dayMid(W, m.end) })),
      });
      if (p.collapsed[key]) continue;
      // de boom: een fase staat als rij als ze zelf in beeld is of er een taak in beeld onder zit, met haar fases erboven
      const shown = new Set<string>();
      const addUp = (fid: string | null | undefined) => { for (const f of faseChain(fid, idx.fases)) if (f.projectId === k.id) shown.add(f.id); };
      for (const f of fases) addUp(f.id);
      for (const t of tasks) addUp(t.faseId);
      const parentOf = (f: Fase) => (f.parentId && shown.has(f.parentId) ? f.parentId : null);
      const order = (o: number | undefined) => o || 1e9; // een nieuwe taak (nog zonder volgorde) achteraan
      const kidsOf = (pid: string | null) => [
        ...[...shown].map((id) => idx.fases.get(id)!).filter((f) => parentOf(f) === pid).map((f) => ({ o: order(f.order), f, t: null })),
        ...tasks.filter((t) => ((t.faseId && shown.has(t.faseId) ? t.faseId : null) === pid)).map((t) => ({ o: order(t.order), f: null, t })),
      ].sort((a, b) => a.o - b.o || (a.f || a.t)!.title.localeCompare((b.f || b.t)!.title));
      const walk = (pid: string | null, lvl: number, seen: Set<string>) => {
        for (const it of kidsOf(pid)) {
          if (it.f) {
            const f = it.f;
            if (seen.has(f.id)) continue; // een lus in de data
            const { l, r } = spanOf(W, f.start, f.end), n = p.waiting.get(f.id) || 0, fk = "f" + f.id, has = kidsOf(f.id).length > 0;
            push({ kind: "fase", key: f.id, y, fase: f, l, r, lvl, coll: !!p.collapsed[fk], has,
              tag: dd(f.end, today) + " · " + f.done + " van " + f.total + " af" + (n ? " · " + nOf(n, "taak wacht", "taken wachten") + " hierop" : "") }, ROW_T);
            if (has && !p.collapsed[fk]) walk(f.id, lvl + 1, new Set([...seen, f.id]));
          } else if (it.t) {
            const o = it.t.owners.map((x) => firstName(personName(x.personId)));
            taskRow(it.t, o.length ? o.join(", ") : "zonder eigenaar", it.t.id, lvl);
          }
        }
      };
      walk(null, 0, new Set());
    }
  } else {
    // medewerker: de eigen rij eerst; Zonder eigenaar achteraan
    const people = [...data.people].sort((a, b) => a.name.localeCompare(b.name)).map((x) => x.id);
    const order: (string | null)[] = [...(p.me.role === "medewerker" ? [p.me.id, ...people.filter((id) => id !== p.me.id)] : people), null];
    for (const pid of order) {
      const tasks = visible.filter((t) => (pid ? t.owners.some((o) => o.personId === pid) : !t.owners.length))
        .sort((a, b) => p.dated(a).start.localeCompare(p.dated(b).start) || a.title.localeCompare(b.title));
      if (!tasks.length) {
        const pp = pid ? idx.people.get(pid) : null;
        if (pp && !filtered) hiddenP.push(pp.first || firstName(pp.name));
        continue;
      }
      const key = "p" + (pid || "none"), ld = pid ? p.load(pid) : null;
      groupRow(key, pid ? personName(pid) : "Zonder eigenaar", ld ? ld.text : "", "p-tls " + (ld ? ld.cls : "t-mute"), null);
      if (p.collapsed[key]) continue;
      for (const t of tasks) taskRow(t, [klantName(t.projectId), padShort(faseChain(t.faseId, idx.fases), 40)].filter(Boolean).join(" · "), t.id + "|" + key);
    }
  }
  return { rows, at, hiddenK, hiddenP, inView: inView.size, height: y };
}

export function Tijdlijn() {
  const { data, error, refetch } = usePlanning();
  const { data: me } = useMe();
  if (!data && error) return <LoadError what="De planningsdata" onRetry={() => refetch()} />;
  if (!data || !me) return <div className="p-loading">Planningsdata laden…</div>;
  return <TijdlijnView data={data} me={me} />;
}

function TijdlijnView({ data, me }: { data: PlanningData; me: Me }) {
  const idx = useIndex(data);
  const { klant } = useAppState();
  const ops = useTaskOps();
  const { data: st } = useSettings();
  const cv = useCapacityView(me.role === "planner" || rightsOf(st?.settings).seeCapacity);
  const { data: clock } = useClockifyLive();
  const [params, setParams] = useSearchParams();
  // groepering, venster, kritisch pad, afhankelijkheden en zoektekst onthoudt de browser
  const [group, setGroup] = usePref<Group>("tp.tl.groep", "klant", oneOf("klant", "persoon"));
  const [win, setWin] = usePref<WinKind>("tp.tl.venster", "m4", oneOf("m4", "w8"));
  const [critOnly, setCritOnly] = usePref<boolean>("tp.tl.kritisch", false, isBool);
  const [showLinks, setShowLinks] = usePref<boolean>("tp.tl.afhankelijkheden", true, isBool);
  const [q, setQ] = usePref<string>("tp.tl.zoek", "", isStr);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [drag, setDrag] = useState<{ id: string; days: number } | null>(null);
  const [held, setHeld] = useState<Record<string, Dated & { n: number }>>({});
  const [focus, setFocus] = useState<{ id: string; retried: boolean } | null>(null);
  const [hl, setHl] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const seq = useRef(0);

  const today = data.today;
  const planner = me.role === "planner";
  const ro = me.role === "medewerker" || !me.canWrite;
  const qq = q.trim().toLowerCase();
  const W = useMemo(() => windowOf(win, today), [win, today]);
  const months = useMemo(() => monthMarks(W), [W]);
  const weeks = useMemo(() => weekMarks(W), [W]);

  // vastgelegde datums: een verschuiving die Planner nog verwerkt, staat al op haar nieuwe plaats
  const dated = useCallback((t: Task): Dated => held[t.id] || t, [held]);
  const nameOf = useCallback((id: string) => {
    const t = idx.tasks.get(id), f = idx.fases.get(id);
    return t ? t.title : f ? "fase " + f.title : "een afgewerkte taak";
  }, [idx]);
  const deps = useMemo(() => {
    const preds = new Map<string, Dep[]>(), waiting = new Map<string, number>();
    for (const d of data.deps) {
      const a = preds.get(d.succ);
      if (a) a.push(d); else preds.set(d.succ, [d]);
      waiting.set(d.pred, (waiting.get(d.pred) || 0) + 1);
    }
    return { preds, waiting };
  }, [data.deps]);

  // een opvolger telt mee als het een open taak is (niet klaar om af te sluiten) bij de gekozen klant; een afgewerkte
  // voorganger of fase heeft geen datum meer om op te wachten
  const isSucc = useCallback((id: string) => { const t = idx.tasks.get(id); return !!t && catOf(t) !== "afsluiten" && (!klant || t.projectId === klant); }, [idx, klant]);
  const dateOf = useCallback<DateOf>((id) => {
    const t = idx.tasks.get(id);
    if (t) return dated(t);
    const f = idx.fases.get(id);
    return f && f.progress < 1 ? f : null;
  }, [idx, dated]);
  // met de lopende sleepbeweging erin, zodat balk, lijnen en rode rand live meebewegen
  const liveOf = useCallback<DateOf>((id) => {
    const d = dateOf(id);
    return d && drag && drag.id === id && drag.days ? { start: addDays(d.start, drag.days), end: addDays(d.end, drag.days) } : d;
  }, [dateOf, drag]);
  // de melding volgt de vastgelegde datums, zodat ze niet verschijnt of verdwijnt terwijl u sleept
  const viol = useMemo(() => findViolations(data.deps, dateOf, isSucc), [data.deps, dateOf, isSucc]);
  const live = useMemo(() => (drag && drag.days ? findViolations(data.deps, liveOf, isSucc) : viol), [drag, data.deps, liveOf, isSucc, viol]);
  const bad = useMemo(() => ({ succ: new Set(live.map((v) => v.dep.succ)), dep: new Set(live.map((v) => v.dep.id)) }), [live]);

  // Clockify: bezetting volgende week per persoon (zoals het prototype: alleen vol en overvol kleuren) en resterende uren
  const load = useCallback((pid: string) => {
    const cp = cv?.people.get(pid), i = cv ? cv.weekOf(addDays(today, 7)) : -1;
    if (!cv || !cp || i < 0) return null;
    const w = cv.load(cp, i), tone = cv.tone(w.pct), label = cv.cap.weeks[i].label;
    if (w.pct == null) return { text: label + " · " + (cp.weeks[i].absent.some((a) => a.kind === "verlof") ? "verlof" : "niet planbaar"), cls: "t-mute" };
    return { text: label + " · " + w.pct + " %", cls: tone === "overvol" ? "t-err" : tone === "vol" ? "t-warn" : "t-mute" };
  }, [cv, today]);
  const lv = clock && clock.clockify.state !== "uit" ? clock : null;
  const restOf = (id: string) => {
    const h = lv?.tasks[id];
    if (!lv || h?.none) return null;
    return h && h.rest != null ? fmtHours(h.rest) + " resterend" : "geen schatting";
  };

  const layout = useMemo(
    () => buildLayout({ data, idx, me, group, W, critOnly, qq, klant, collapsed, dated, waiting: deps.waiting, load }),
    [data, idx, me, group, W, critOnly, qq, klant, collapsed, dated, deps.waiting, load],
  );

  // verbindingslijnen tussen zichtbare voorgangers en opvolgers; rood als de opvolger te vroeg begint
  const links = useMemo(() => {
    const out: { key: string; cls: string; l: string; t: number; w: string; h: number }[] = [];
    if (!showLinks) return out;
    const xOf = (id: string, d: Dated) => (idx.tasks.get(id)?.milestone ? spanOf(W, d.end, d.end) : spanOf(W, d.start, d.end));
    for (const d of data.deps) {
      const ya = layout.at.get(d.pred), yb = layout.at.get(d.succ), da = liveOf(d.pred), db = liveOf(d.succ);
      if (ya == null || yb == null || !da || !db) continue;
      const a = xOf(d.pred, da), b = xOf(d.succ, db), cls = "p-tll" + (bad.dep.has(d.id) ? " bad" : "");
      const xm = Math.min(99.6, a.r + 0.35), lo = Math.min(xm, b.l), hi = Math.max(xm, b.l);
      out.push({ key: d.id + "a", cls, l: pct(a.r), t: ya - 1, w: pct(Math.max(0.2, xm - a.r)), h: 2 });
      out.push({ key: d.id + "b", cls, l: pct(xm), t: Math.min(ya, yb) - 1, w: "2px", h: Math.abs(yb - ya) + 2 });
      out.push({ key: d.id + "c", cls, l: pct(lo), t: yb - 1, w: pct(Math.max(0.2, hi - lo)), h: 2 });
    }
    return out;
  }, [showLinks, data.deps, layout, liveOf, idx, W, bad]);

  const open = useCallback((id: string) => setParams((p) => { p.delete("fase"); p.set("taak", id); return p; }), [setParams]);
  const openFase = useCallback((id: string) => setParams((p) => { p.delete("taak"); p.set("fase", id); return p; }), [setParams]);
  const closeTask = () => setParams((p) => { p.delete("taak"); return p; });

  // nieuwe datums meteen tonen tot Planner ze bevestigt (of de wijziging mislukt); geeft de functie terug die ze loslaat
  const hold = (moves: { task: Task; start: string; end: string }[]) => {
    const n = ++seq.current;
    setHeld((h) => { const x = { ...h }; for (const m of moves) x[m.task.id] = { start: m.start, end: m.end, n }; return x; });
    return () => setHeld((h) => {
      const x = { ...h };
      let changed = false;
      for (const m of moves) if (x[m.task.id]?.n === n) { delete x[m.task.id]; changed = true; }
      return changed ? x : h;
    });
  };
  const shift = (id: string, days: number) => {
    const t = idx.tasks.get(id);
    if (!t) return;
    const c = dated(t), start = addDays(c.start, days), end = addDays(c.end, days);
    const release = hold([{ task: t, start, end }]);
    ops.run(t, [{ kind: "dates", start, end }], (days > 0 ? "Later gezet" : "Eerder gezet") + ": " + t.title + ", nu " + ddmm(start) + " tot " + ddmm(end)).catch(() => {}).finally(release);
  };
  const fix = (list: Violation[]) => {
    const moves = [...fixShifts(list, data.deps, dateOf, isSucc)].flatMap(([id, n]) => {
      const t = idx.tasks.get(id);
      if (!t) return [];
      const c = dated(t);
      return [{ task: t, start: addDays(c.start, n), end: addDays(c.end, n) }];
    });
    if (!moves.length) return;
    const msg = moves.length === 1 ? moves[0].task.title + " start nu na " + (list.length === 1 ? nameOf(list[0].dep.pred) : "de voorgangers") : moves.length + " opvolgers na hun voorganger gezet";
    const items = moves.map((m) => ({ task: m.task, ops: [{ kind: "dates", start: m.start, end: m.end } as TaskOp] }));
    const release = hold(moves);
    (items.length === 1 ? ops.run(items[0].task, items[0].ops, msg).then(() => {}) : ops.runMany(items, msg)).catch(() => {}).finally(release);
  };
  // naar een taak scrollen (groep openklappen indien nodig), bv. de opvolger uit de melding
  const show = (id: string) => {
    const t = idx.tasks.get(id);
    if (!t) return;
    setCollapsed((c) => {
      const x = { ...c };
      if (group === "klant") { delete x["k" + t.projectId]; for (const f of faseChain(t.faseId, idx.fases)) delete x["f" + f.id]; }
      else (t.owners.length ? t.owners.map((o) => "p" + o.personId) : ["pnone"]).forEach((k) => delete x[k]);
      return x;
    });
    setFocus({ id, retried: false });
  };
  // staat de rij er niet (zoekfilter, alleen kritisch pad), dan eerst die filters wissen; anders de taak openen
  useEffect(() => {
    if (!focus) return;
    const row = layout.rows.find((r) => r.kind === "task" && r.task.id === focus.id), el = bodyRef.current;
    if (row && el) {
      el.scrollTo({ top: Math.max(0, row.y - el.clientHeight / 2 + 60), behavior: document.visibilityState === "visible" ? "smooth" : "auto" });
      setHl(focus.id);
      setFocus(null);
    } else if (!focus.retried && (qq || critOnly)) {
      setQ("");
      setCritOnly(false);
      setFocus({ id: focus.id, retried: true });
    } else {
      setFocus(null);
      open(focus.id);
    }
  }, [focus, layout, qq, critOnly, open]);
  useEffect(() => {
    if (!hl) return;
    const t = window.setTimeout(() => setHl(null), 1600);
    return () => window.clearTimeout(t);
  }, [hl]);

  // slepen: de balk vangt de aanwijzer, zodat bewegen buiten de balk blijft werken; zonder beweging is het een klik.
  // De handlers zijn stabiel (de rijen zijn gememoriseerd) en lezen de actuele waarden uit `act`.
  const act = useRef({ open, shift, ro, today, days: W.days });
  useEffect(() => { act.current = { open, shift, ro, today, days: W.days }; });
  const dragRef = useRef<{ id: string; x0: number; perDay: number; min: number; days: number; moved: boolean; ro: boolean } | null>(null);
  const onDown = useCallback((e: PointerEvent<HTMLElement>, id: string, end: string) => {
    if (e.button !== 0) return;
    const a = act.current, el = e.currentTarget;
    // een deadline slepen tot vóór vandaag kan niet: dan hoort de taak in Opschonen
    dragRef.current = { id, x0: e.clientX, perDay: (el.parentElement?.offsetWidth || 1) / a.days, min: diffDays(end, a.today), days: 0, moved: false, ro: a.ro };
    if (!a.ro) {
      try { el.setPointerCapture(e.pointerId); } catch { /* oudere browsers */ }
    }
  }, []);
  const onMove = useCallback((e: PointerEvent<HTMLElement>) => {
    const d = dragRef.current;
    if (!d || d.ro) return;
    const dx = e.clientX - d.x0;
    if (!d.moved && Math.abs(dx) <= 3) return;
    const days = Math.max(d.min, Math.round(dx / d.perDay));
    if (d.moved && days === d.days) return;
    d.moved = true;
    d.days = days;
    setDrag({ id: d.id, days });
  }, []);
  const onUp = useCallback((id: string) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d || d.id !== id) return;
    if (!d.moved) { act.current.open(id); return; }
    setDrag(null);
    if (d.days) act.current.shift(id, d.days);
  }, []);
  const onCancel = useCallback(() => { dragRef.current = null; setDrag(null); }, []);
  const onOpen = useCallback((id: string) => act.current.open(id), []);
  const toggle = useCallback((key: string) => setCollapsed((c) => ({ ...c, [key]: !c[key] })), []);

  const sel = params.get("taak");
  const selTask = sel ? data.tasks.find((t) => t.id === sel) : null;
  const todayL = pct(dayMid(W, today));
  const overTotal = useMemo(() => data.tasks.filter((t) => catOf(t) === "actief" && t.end < today && (!klant || t.projectId === klant)).length, [data.tasks, today, klant]);

  // melding: opvolgers die beginnen vóór hun voorganger klaar is
  const v0 = viol[0], succs = new Set(viol.map((v) => v.dep.succ)).size;
  const v0Title = v0 ? idx.tasks.get(v0.dep.succ)?.title || "" : "";
  const violText = !v0 ? "" : viol.length === 1 ? v0Title + " begint op " + ddmm(v0.ss) + ", vóór " + nameOf(v0.dep.pred) + " klaar is op " + ddmm(v0.pe) + "."
    : succs === 1 ? v0Title + " begint vóór " + viol.length + " voorgangers klaar zijn." : succs + " opvolgers beginnen vóór hun voorganger klaar is.";

  // voet: wat buiten beeld staat
  const few = (names: string[], what: string) => (names.length <= 3 ? andList(names) : names.length + " " + what);
  const filters = [klant ? "klant " + (idx.klanten.get(klant)?.name || "") : "", qq ? "zoek “" + q.trim() + "”" : "", critOnly ? "kritisch pad" : ""].filter(Boolean);
  let hiddenText: string, hiddenAll: string;
  if (group === "klant") {
    const past = layout.hiddenK.filter((k) => k.start !== k.finish).map((k) => k.name), noSpan = layout.hiddenK.filter((k) => k.start === k.finish).map((k) => k.name);
    const parts = [past.length ? few(past, "klanten") + ", einde voorbij" : "", noSpan.length ? few(noSpan, "klanten") + ", zonder looptijd" : ""].filter(Boolean);
    hiddenText = parts.length ? "Buiten beeld: " + parts.join(" · ") : filters.length ? "Gefilterd op " + andList(filters) : "Alle klanten staan in beeld";
    hiddenAll = [...past, ...noSpan].join(", ");
  } else {
    hiddenText = layout.hiddenP.length ? "Zonder taken in deze periode: " + few(layout.hiddenP, "collega's") : filters.length ? "Gefilterd op " + andList(filters) : "Iedereen heeft taken in deze periode";
    hiddenAll = layout.hiddenP.join(", ");
  }
  const hint = !ro ? "sleep een balk om start en deadline samen te verschuiven, klik om de taak te openen"
    : me.role === "medewerker" ? "klik op een balk om de taak te openen; verschuiven doet de planner" : "klik op een balk om de taak te openen; verschuiven doet u in Planner";
  const headSub = "Tijdlijn " + (group === "klant" ? "per klant" : "per persoon") + " · " + MONL[Number(W.from.slice(5, 7)) - 1] + " tot " + MONL[Number(W.to.slice(5, 7)) - 1] +
    " · vandaag " + ddmm(today) + " · " + nOf(layout.inView, "taak", "taken") + " in beeld";

  return (
    <>
      <div className="p-head">
        <div><h1>Planning</h1><p>{headSub}</p></div>
        <div className="p-tools"><label className="ex-search p-search"><Icon name="search" size={15} /><input type="text" placeholder="Zoek taak, klant of route" aria-label="Zoek taak, klant of route" value={q} onChange={(e) => setQ(e.target.value)} /></label></div>
      </div>
      <div className="p-tabrow">
        <PlanningTabs current="tijdlijn" />
        <div className="p-tools">
          <div className="ex-seg" role="group" aria-label="Groeperen">
            <button type="button" aria-pressed={group === "klant"} onClick={() => setGroup("klant")}>Per klant</button>
            <button type="button" aria-pressed={group === "persoon"} onClick={() => setGroup("persoon")}>Per persoon</button>
          </div>
          <div className="ex-seg" role="group" aria-label="Periode">
            <button type="button" aria-pressed={win === "m4"} onClick={() => setWin("m4")}>4 maanden</button>
            <button type="button" aria-pressed={win === "w8"} onClick={() => setWin("w8")}>8 weken</button>
          </div>
          <label className="ex-check p-check"><input type="checkbox" checked={critOnly} onChange={() => setCritOnly(!critOnly)} />Alleen kritisch pad</label>
          <label className="ex-check p-check"><input type="checkbox" checked={showLinks} onChange={() => setShowLinks(!showLinks)} />Afhankelijkheden</label>
        </div>
      </div>
      {v0 && (
        <div className="ex-notice ex-notice--warning p-notice p-cap-note"><Icon name="alert" size={16} />
          <p>{violText}</p>
          <button type="button" className="ex-btn-xs" onClick={() => show(v0.dep.succ)}>Toon</button>
          {!ro && <button type="button" className="ex-btn-xs ex-btn-xs--primary" onClick={() => fix(viol)}>{succs > 1 ? "Alles rechtzetten" : viol.length > 1 ? "Na de voorgangers zetten" : "Na de voorganger zetten"}</button>}
        </div>
      )}
      <section className="p-card p-tl">
        <div className="p-tlbody" ref={bodyRef}>
          <div className="p-tlh">
            <div className="p-tlh-l">{group === "klant" ? "Klant · taken vanaf vandaag" : "Persoon · taken vanaf vandaag"}</div>
            <div className="p-tlh-r">
              <div className="p-tlm">{months.map((m) => <span key={m.key} style={{ left: m.l, width: m.w }}>{m.label}</span>)}</div>
              <div className="p-tlw">{weeks.map((w) => <span key={w.key} style={{ left: w.l }}>{w.label}</span>)}</div>
              <span className="p-tltoday" style={{ left: todayL }}>vandaag</span>
            </div>
          </div>
          <div className="p-tlinner" style={{ height: layout.height }}>
            <div className="p-tlgrid">{weeks.map((w) => <i key={w.key} style={{ left: w.l }} />)}<b className="p-tlnow" style={{ left: todayL }} /></div>
            {layout.rows.map((r) => {
              if (r.kind === "group") return <GroupRowView key={r.key} r={r} planner={planner} today={today} onToggle={toggle} />;
              if (r.kind === "fase") return <FaseRowView key={r.key} r={r} onToggle={toggle} onOpen={openFase} />;
              const t = r.task, c = dated(t), dr = drag?.id === t.id, pr = deps.preds.get(t.id);
              return (
                <TaskRowView key={r.key} task={t} start={c.start} end={c.end} dragging={dr} days={dr ? drag.days : 0} y={r.y} W={W} today={today}
                  sub={r.sub} owners={r.owners} klantName={r.klantName} lvl={r.lvl} preds={pr?.length || 0} waitFor={pr ? nameOf(pr[0].pred) : null} hours={restOf(t.id)}
                  bad={bad.succ.has(t.id)} ro={ro} hl={hl === t.id} onDown={onDown} onMove={onMove} onUp={onUp} onCancel={onCancel} onOpen={onOpen} />
              );
            })}
            <div className="p-tllinks">{links.map((s) => <i key={s.key} className={s.cls} style={{ left: s.l, top: s.t, width: s.w, height: s.h }} />)}</div>
          </div>
          {!layout.rows.length && <p className="p-empty" style={{ padding: "12px 12px 0" }}>{filters.length ? "Geen taken met deze filters in deze periode." : "Geen taken in deze periode."}</p>}
        </div>
        <div className="p-tlf">
          <span>{overTotal ? nOf(overTotal, "taak", "taken") + " over deadline " + (overTotal === 1 ? "staat" : "staan") + " niet in beeld." : "Geen actieve taken over deadline."}
            {planner && overTotal > 0 && <> <Link className="ex-link" to="/opschonen">Naar Opschonen</Link></>}</span>
          <span className="p-tlleg">
            <span><i className="t" />Taak</span><span><i className="c" />Kritisch pad</span><span><i className="p" />Geparkeerd</span><span><i className="f" />Fase</span>
            <span><i className="m" />Mijlpaal</span><span><i className="d" />Einde plan</span><span><i className="l" />Afhankelijkheid</span><span><i className="n" />Vandaag</span>
          </span>
        </div>
        <div className="p-tlf2" title={hiddenAll || undefined}>{hiddenText} · {hint}</div>
      </section>
      {selTask && <TaskDrawer key={selTask.id} task={selTask} data={data} onClose={closeTask} />}
    </>
  );
}

const GroupRowView = memo(function GroupRowView({ r, planner, today, onToggle }: { r: GroupRow; planner: boolean; today: string; onToggle: (key: string) => void }) {
  const k = r.plan;
  const over = k && k.over > 0 ? <><Icon name="alert" size={12} />{k.over} over deadline</> : null;
  return (
    <div className="p-tlr grp">
      <div className="p-tll-l">
        <button type="button" className="p-tlg" aria-expanded={!r.coll} onClick={() => onToggle(r.key)}>
          <Icon name={r.coll ? "right" : "down"} size={14} /><span className="n">{r.label}</span><span className={r.statusCls}>{r.status}</span>
        </button>
      </div>
      <div className="p-tl-lane">
        {k?.track && <span className="p-tlk" style={{ left: k.track.l, width: k.track.w }} />}
        {k?.milestones.map((m) => (
          <span key={m.id} className={"p-tlms" + (m.done ? " done" : m.late ? " late" : "")} style={{ left: pct(m.x) }}>
            <span className={"p-tip" + (r.y < 60 ? " below" : "") + (m.x > 80 ? " end" : m.x < 8 ? " start" : "")} role="tooltip">
              <b>Mijlpaal · {dd(m.end, today)}</b><span>{m.title}</span><span>{m.done ? "gehaald" : m.late ? "niet gehaald" : "gepland"}</span>
            </span>
          </span>
        ))}
        {k?.end && <span className={"p-tld" + (k.end.past ? " past" : "")} style={{ left: k.end.l }} />}
        {k?.endTag && <span className={k.endTag.cls} style={{ left: k.endTag.left, right: k.endTag.right }}>{k.endTag.text}</span>}
        {k && over && (planner
          ? <Link className="p-tlover" to="/opschonen" style={{ left: k.overLeft, right: k.overRight }} title="Naar Opschonen">{over}</Link>
          : <span className="p-tlover ro" style={{ left: k.overLeft, right: k.overRight }}>{over}</span>)}
      </div>
    </div>
  );
});

// een rij inspringen per laag van fases (hoogstens vier stappen)
const indent = (base: number, lvl: number) => ({ paddingLeft: base + Math.min(lvl, 4) * 16 });

const FaseRowView = memo(function FaseRowView({ r, onToggle, onOpen }: { r: FaseRow; onToggle: (key: string) => void; onOpen: (id: string) => void }) {
  const tag = tagAt(r.l, r.r);
  return (
    <div className="p-tlr sub">
      <div className="p-tll-l" style={indent(16, r.lvl)}>
        <span className="p-tlf-n">
          {r.has
            ? <button type="button" className="p-tlf-tog" aria-expanded={!r.coll} aria-label={(r.coll ? "Openklappen: " : "Inklappen: ") + r.fase.title} onClick={() => onToggle("f" + r.fase.id)}><Icon name={r.coll ? "right" : "down"} size={13} /></button>
            : <span className="p-tlf-tog" />}
          <span className="p-fase">Fase</span>
          <button type="button" className="p-tlt n" title={"Fase openen: " + r.fase.title} onClick={() => onOpen(r.fase.id)}>{r.fase.title}</button>
        </span>
      </div>
      <div className="p-tl-lane">
        <span className="p-tlfb" style={{ left: pct(r.l), width: pct(Math.max(0.6, r.r - r.l)) }} />
        <span className={tag.cls} style={{ left: tag.left, right: tag.right }}>{r.tag}</span>
      </div>
    </div>
  );
});

interface TaskRowProps {
  task: Task; start: string; end: string; dragging: boolean; days: number; y: number; W: Win; today: string;
  sub: string; owners: string; klantName: string; lvl: number; preds: number; waitFor: string | null; bad: boolean; ro: boolean; hl: boolean;
  /** Clockify: "12 u resterend" of "geen schatting"; null zonder Clockify */
  hours: string | null;
  onDown: (e: PointerEvent<HTMLElement>, id: string, end: string) => void; onMove: (e: PointerEvent<HTMLElement>) => void;
  onUp: (id: string) => void; onCancel: () => void; onOpen: (id: string) => void;
}

/** Eén taak: titel links, balk (of ruit voor een mijlpaal) met tooltip en label rechts. Start en einde: vastgelegd, plus `days` tijdens het slepen. */
const TaskRowView = memo(function TaskRowView(p: TaskRowProps) {
  const t = p.task, park = catOf(t) === "geparkeerd";
  const s = p.days ? addDays(p.start, p.days) : p.start, e = p.days ? addDays(p.end, p.days) : p.end;
  const { l, r } = t.milestone ? spanOf(p.W, e, e) : spanOf(p.W, s, e);
  const tag = tagAt(l, r);
  const cls = "p-tlb" + (t.milestone ? " ms" : "") + (t.critical && !park ? " crit" : park ? " park" : "") + (p.dragging ? " drag" : "") + (p.bad ? " bad" : "") + (p.ro ? " ro" : "") + (t.pending ? " pending" : "");
  // tooltip onder de balk in de bovenste rijen, en aan de rand uitgelijnd zodat hij binnen de tijdlijn blijft
  const tipCls = "p-tip" + (p.y < 60 ? " below" : "") + (r > 80 ? " end" : l < 8 ? " start" : "");
  const when = (t.milestone ? "mijlpaal " + dd(e, p.today) : dd(s, p.today) + " tot " + dd(e, p.today)) + (p.dragging && p.days ? " · " + (p.days > 0 ? "+" : "") + p.days + " d" : "");
  return (
    <div className={"p-tlr sub" + (p.hl ? " hl" : "")}>
      <div className="p-tll-l" style={p.lvl ? indent(36, p.lvl) : undefined}><button type="button" className="p-tlt" title={t.title} onClick={() => p.onOpen(t.id)}>{t.title}</button></div>
      <div className="p-tl-lane">
        <span className={cls} style={t.milestone ? { left: pct((l + r) / 2) } : { left: pct(l), width: pct(Math.max(0.6, r - l)) }}
          onPointerDown={(ev) => p.onDown(ev, t.id, p.end)} onPointerMove={p.onMove} onPointerUp={() => p.onUp(t.id)} onPointerCancel={p.onCancel}>
          <span className={tipCls} role="tooltip">
            <b>{when}</b>
            <span>{t.title}</span>
            <span>{p.klantName} · {p.owners}{t.critical ? " · kritisch pad" : ""}{park ? " · geparkeerd" : ""}</span>
            <span>{p.hours ? p.hours + " · " : ""}{p.waitFor ? "wacht op " + p.waitFor + " · " : ""}{p.ro ? "klik om te openen" : "sleep om te verschuiven"}</span>
          </span>
        </span>
        <span className={tag.cls} style={{ left: tag.left, right: tag.right }}>{dd(e, p.today)} · {p.sub}{park ? " · geparkeerd" : ""}{p.preds ? " · " + nOf(p.preds, "voorganger", "voorgangers") : ""}</span>
      </div>
    </div>
  );
});
